// src/pages/lkp/LookupEntityWorkspace.tsx
//
// Step 3 — FINALIZE. The entity is the spine; here the SME finalizes:
//   • the entity name (final)
//   • the binding fields (which physical fields belong to the entity) + provenance
//   • the canonical value set + source→canonical crosswalk (via EntityEditor)
//
// Cross-certification with Steps 1 & 2 is shown per binding. A binding that appears
// here but was NOT accepted in Step 1 or confirmed in Step 2 is flagged "needs
// attention" with a jump back to the right step. Re-running Discovery/Reference MERGES
// (bindings + source evidence) and never overwrites canonical/crosswalk the SME authored.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  lookup_getEntities, lookup_getEntity, lookup_rebuildEntities, lookup_foldAuthoritative,
  lookup_entityAction,
  type Buckets, type EntitiesResponse, type EntityActionName,
} from "../api/lkpEntityAPI";
import { lookup_getCandidates, lookup_getAuthoritative } from "../api/lkpAPI";
import EntityHarmonizePanel from "./EntityHarmonizePanel";
import EntityEditor from "./EntityEditor";
import type { StepActions } from "./LookupWorkbench";   // SIMPLE-UI v2: actions shown in the top bar
import { lookup_promote, lookup_depromote, lookup_promoteAllInEntity } from "../api/lkpAPI";
import { STATUS_META, CSS, valueOverlap } from "./entityShared";

interface Props {
  appNm: string; entityId: string; entityLabel?: string; onGoStep?: (n: 1 | 2) => void;
  onActions?: (a: StepActions) => void;   // registers this step's menu items in the top bar
  reloadSignal?: number;                  // bumped by the workbench after a publish
}
export type BindingCert = { s1: boolean; s2: boolean; certified: boolean; s1status?: string; state?: "accepted" | "rejected" | "pending" };
const up = (s?: string) => (s || "").toUpperCase();
const STATUS_DESC: Record<string, string> = {
  discovered: "Proposed by Step 1, not yet accepted.",
  sourced: "Has source evidence (data and/or client) — canonical set not finalized yet.",
  harmonized: "Canonical set authored and the crosswalk covers all source values.",
  published: "Governed set published for the rule engine.",
};

export default function LookupEntityWorkspace({ appNm, entityId, entityLabel, onGoStep, onActions, reloadSignal }: Props) {
  const [data, setData] = useState<EntitiesResponse | null>(null);
  const [selId, setSelId] = useState<string | null>(null);
  const [buckets, setBuckets] = useState<Buckets | null>(null);
  const [cert, setCert] = useState<Map<string, BindingCert>>(new Map());
  const [showHarmonize, setShowHarmonize] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ m: string; e?: boolean } | null>(null);
  const [q, setQ] = useState("");
  const [source, setSource] = useState("");   // secondary lens (see SOURCE_FILTERS)
  const [view, setView] = useState<"all" | "ready" | "attention" | "needsvalues" | "published" | "profile" | "client" | "both" | "aligned" | "clientvals" | "datavals" | "novalues">("all");
  const note = (m: string, e = false) => { setFlash({ m, e }); setTimeout(() => setFlash(null), 3500); };

  // entities + Step-1 candidates + Step-2 authoritative → per-binding certification
  const load = useCallback(async () => {
    try {
      const [r, c, a] = await Promise.allSettled([
        lookup_getEntities(entityId), lookup_getCandidates(entityId), lookup_getAuthoritative(entityId),
      ]);
      const ents = r.status === "fulfilled" ? r.value : null;
      setData(ents);
      if (ents && !selId && ents.entities.length) setSelId(ents.entities[0].entity_id);

      const s1 = new Map<string, string>();               // fieldKey -> Step-1 status
      if (c.status === "fulfilled") (c.value.data?.items || []).forEach((it: any) => {
        const ref = it.family === "lookup_field" ? it.field : it.family === "code_description_pair" ? it.code : null;
        if (ref) s1.set(up(`${ref.table}.${ref.field}`), it.status || "PROPOSED");
      });
      const s2 = new Map<string, boolean>();               // fieldKey -> Step-2 confirmed
      if (a.status === "fulfilled") (a.value.data?.definitions || []).forEach((d: any) => {
        if (d.logical_field) s2.set(up(`${d.table}.${d.column}`), !!(d.review && d.review.confirmed));
      });
      const m = new Map<string, BindingCert>();
      const keys = new Set<string>([...s1.keys(), ...s2.keys()]);
      (ents?.entities || []).forEach((e) => e.bindings.forEach((b) => keys.add(up(b.field_id))));
      keys.forEach((k) => {
        const a1 = s1.get(k) === "ACCEPTED", a2 = s2.get(k) === true;
        const certified = a1 || a2;
        const rejected = !certified && s1.get(k) === "REJECTED";
        const state: "accepted" | "rejected" | "pending" = certified ? "accepted" : rejected ? "rejected" : "pending";
        m.set(k, { s1: a1, s2: a2, certified, s1status: s1.get(k), state });
      });
      setCert(m);
    } catch { setData(null); }
  }, [entityId, selId]);
  useEffect(() => { load(); }, [entityId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadBuckets = useCallback(async (leid: string) => {
    try { const r = await lookup_getEntity(entityId, leid); setBuckets(r.buckets); } catch { setBuckets(null); }
  }, [entityId]);
  useEffect(() => { if (selId) loadBuckets(selId); }, [selId, loadBuckets, data]);

  // AUTO-RELOAD (replaces the old manual "Refresh" button). Step 1/2 decisions are made on other
  // screens, so this view can go stale. Reload whenever the SME returns to it — on window focus and
  // when the tab becomes visible again — so the counts and work queue are always current.
  useEffect(() => {
    const reload = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("focus", reload);
    document.addEventListener("visibilitychange", reload);
    return () => {
      window.removeEventListener("focus", reload);
      document.removeEventListener("visibilitychange", reload);
    };
  }, [load]);

  const entities = data?.entities || [];
  const selected = entities.find((e) => e.entity_id === selId) || null;
  const reviewer = "SME";   // TODO: wire to auth when available
  const doPromote = async (code: string, promote: boolean) => {
    if (!selId) return;
    try {
      if (promote) await lookup_promote(entityId, [{ entity_id: selId, code }], reviewer);
      else await lookup_depromote(entityId, [{ entity_id: selId, code }], reviewer);
      await load();
    } catch (e) { /* surfaced by load */ }
  };
  const doPromoteApproved = async () => {
    if (!selId) return;
    try { await lookup_promoteAllInEntity(entityId, selId, "Approved", reviewer); await load(); }
    catch (e) { /* */ }
  };

  const entityCert = useCallback((e: any) => {
    const items = (e.bindings || []).map((b: any) => cert.get(up(b.field_id)) || { s1: false, s2: false, certified: false, state: "pending" as const });
    const uncert = items.filter((x: BindingCert) => !x.certified).length;
    const rejected = items.filter((x: BindingCert) => x.state === "rejected").length;
    const pending = items.filter((x: BindingCert) => x.state === "pending").length;
    const s1any = items.some((x: BindingCert) => x.s1); const s2any = items.some((x: BindingCert) => x.s2);
    return { total: items.length, certified: items.length - uncert, uncertified: uncert, pending, rejected, s1any, s2any };
  }, [cert]);
  const attentionTotal = useMemo(() => entities.reduce((n, e) => n + (entityCert(e).uncertified > 0 ? 1 : 0), 0), [entities, entityCert]);
  const readyNoCanon = useCallback((e: any) => entityCert(e).uncertified === 0 && e.status !== "published" && (e.canonical_values?.length ?? 0) === 0, [entityCert]);
  const needsValuesTotal = useMemo(() => entities.reduce((n, e) => n + (readyNoCanon(e) ? 1 : 0), 0), [entities, readyNoCanon]);
  const originOf = (e: any) => {
    const v = valueOverlap(e);                       // by VALUES (data vs client), not bindings
    return {
      profileOnly: v.prof > 0 && v.cli === 0,
      clientOnly: v.cli > 0 && v.prof === 0,
      both: v.prof > 0 && v.cli > 0,
      none: v.prof === 0 && v.cli === 0,
    };
  };

  const visible = entities.filter((e) => {
    const ec = entityCert(e);
    // WORK STATE — the primary axis: does this entity still need the SME?
    //   Needs me = a binding isn't accepted/confirmed yet, OR there are no governed values yet.
    //   Ready    = bindings confirmed AND values authored (these are what flow to EKD).
    const needsMe = ec.uncertified > 0 || originOf(e).none;
    if (view === "attention" && !needsMe) return false;
    if (view === "ready" && needsMe) return false;

    // SOURCE LENS — secondary filter on where the values came from / how they compare.
    if (source) {
      const o = originOf(e); const v = valueOverlap(e);
      if (source === "profile" && !o.profileOnly) return false;
      if (source === "client" && !o.clientOnly) return false;
      if (source === "both" && !o.both) return false;
      if (source === "novalues" && !o.none) return false;
      if (source === "aligned" && !(v.comparable && v.clientOnly === 0 && v.profileOnly === 0)) return false;
      if (source === "clientvals" && !(v.comparable && v.clientOnly > 0)) return false;
      if (source === "datavals" && !(v.comparable && v.profileOnly > 0)) return false;
    }

    if (q) { const s = q.toLowerCase(); return e.name.toLowerCase().includes(s) || e.bindings.some((b: any) => b.field_id.toLowerCase().includes(s)); }
    return true;
  });

  const act = async (action: EntityActionName, payload: Record<string, any> = {}, tag: string = action) => {
    if (!selId && action !== "create") return;
    setBusy(tag);
    try {
      const r = await lookup_entityAction(entityId, action, { entity_id: selId || undefined, payload });
      await load(); if (r.buckets) setBuckets(r.buckets); else if (selId) await loadBuckets(selId);
      note("Saved.");
    } catch (e: any) { note(e?.detail || "Action failed", true); } finally { setBusy(null); }
  };

  // SIMPLE-UI v2: this step's occasional actions go into the workbench's single "More" menu;
  // its primary action (Publish to EKD & BC) is rendered by the workbench top bar.
  useEffect(() => {
    if (!onActions) return;
    onActions({
      menu: [
        { label: "Harmonize entity names", onClick: () => setShowHarmonize(true), disabled: !!busy },
        { label: "+ New lookup entity", disabled: !!busy, onClick: () => {
            const nm = prompt("New lookup entity name (e.g. Country Lookup)"); if (nm) act("create", { name: nm }, "create"); } },
      ],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy, onActions, selId]);
  // reload after a publish from the top bar
  useEffect(() => { if (reloadSignal) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [reloadSignal]);

  // Three work states answer the only question that matters: does this entity still need me?
  const VIEWS: Array<{ k: typeof view; label: string; n?: number; hint?: string }> = [
    { k: "attention", label: "Needs me", n: attentionTotal + needsValuesTotal,
      hint: "bindings not yet accepted, or no governed values yet" },
    { k: "ready", label: "Ready", n: entities.filter((e) => entityCert(e).uncertified === 0 && !originOf(e).none).length,
      hint: "bindings confirmed and governed values authored — these flow to EKD" },
    { k: "all", label: "All", n: entities.length },
  ];

  // Everything else (where the values came from / how they compare) is a secondary lens, not a
  // top-level navigation axis. One dropdown instead of ten chips.
  const SOURCE_FILTERS: Array<{ k: string; label: string; n: number }> = [
    { k: "", label: "Any source", n: entities.length },
    { k: "profile", label: "From data only", n: entities.filter((e) => originOf(e).profileOnly).length },
    { k: "client", label: "From client list only", n: entities.filter((e) => originOf(e).clientOnly).length },
    { k: "both", label: "From both", n: entities.filter((e) => originOf(e).both).length },
    { k: "novalues", label: "No values yet", n: entities.filter((e) => originOf(e).none).length },
    { k: "aligned", label: "Values agree", n: entities.filter((e) => { const v = valueOverlap(e); return v.comparable && v.clientOnly === 0 && v.profileOnly === 0; }).length },
    { k: "clientvals", label: "Client has extra values", n: entities.filter((e) => { const v = valueOverlap(e); return v.comparable && v.clientOnly > 0; }).length },
    { k: "datavals", label: "Data has extra values", n: entities.filter((e) => { const v = valueOverlap(e); return v.comparable && v.profileOnly > 0; }).length },
  ];
  return (
    <div className="lew">
      <style>{CSS}</style>
      <style>{EXTRA}</style>
      {flash && <div className={"flash " + (flash.e ? "err" : "ok")}>{flash.m}</div>}
      {/* The work queue — one actionable line instead of two ambient warnings. Clicking it
          switches to the "Needs me" view, which IS this list. */}
      {(attentionTotal > 0 || needsValuesTotal > 0) && view !== "attention" && (
        <div className="lew-queue" onClick={() => setView("attention")}>
          <b>{attentionTotal + needsValuesTotal}</b> need your attention
          {attentionTotal > 0 && <span> · {attentionTotal} awaiting a confirmed column</span>}
          {needsValuesTotal > 0 && <span> · {needsValuesTotal} with no governed values yet</span>}
          <span className="lew-queue-go">Review these →</span>
        </div>
      )}
      {view === "attention" && attentionTotal > 0 && onGoStep && (
        <div className="lew-queue-help">
          Columns are accepted in Step 1 or confirmed in Step 2.
          <button className="lew-attn-b" onClick={() => onGoStep(1)}>Go to Step 1</button>
          <button className="lew-attn-b" onClick={() => onGoStep(2)}>Go to Step 2</button>
        </div>
      )}
      {data?.summary && (
        <div className="summary">
          <b>{data.summary.entities}</b> entities · <b>{data.summary.bound_fields}</b> bound fields ·
          <span className="warn"> {data.summary.unmapped_source_values}</span> unmapped source values
          {data.authoritative_proposals?.length ? <span className="prop"> · {data.authoritative_proposals.length} mapping proposal(s)</span> : null}
        </div>
      )}

      <div className="lew-body">
        <div className="lew-list">
          <div className="lew-search"><input placeholder="Search entities / fields…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
          <div className="lew-filters">
            {VIEWS.map((v) => (
              <button key={v.k} title={v.hint}
                className={"pill " + (view === v.k ? "on" : "") + (v.k === "attention" && (v.n || 0) > 0 ? " attn" : "")}
                onClick={() => setView(v.k)}>{v.label}{v.n != null ? ` (${v.n})` : ""}</button>
            ))}
            <select className="lew-src" value={source} onChange={(e) => setSource(e.target.value)}
              title="Filter by where the values came from">
              {SOURCE_FILTERS.map((f) => <option key={f.k} value={f.k}>{f.label} ({f.n})</option>)}
            </select>
          </div>
          <div className="lew-items">
            {visible.map((e) => {
              const ec = entityCert(e);
              return (
                <button key={e.entity_id} className={"lew-item " + (selId === e.entity_id ? "on" : "")} onClick={() => setSelId(e.entity_id)}>
                  <div className="li-top"><span className="li-name">{e.name}</span>
                    {/* Work state, not the internal lifecycle. "Sourced" was automatic and told the
                        SME nothing; this says whether the entity still needs her. */}
                    {(ec.uncertified > 0 || originOf(e).none)
                      ? <span className="li-status li-needs" title={ec.uncertified > 0 ? "A bound column is not accepted/confirmed yet" : "No governed values authored yet"}>needs me</span>
                      : <span className="li-status li-ready" title="Bindings confirmed and governed values authored — flows to EKD">ready</span>}
                  </div>
                  <div className="li-meta">
                    <span>{e.bindings.length} field{e.bindings.length === 1 ? "" : "s"}</span>
                    <span>· {e.buckets?.canonical ?? e.canonical_values.length} canonical</span>
                    {ec.uncertified > 0
                      ? <>
                          {ec.pending > 0 && <span className="li-attn">· ⚠ {ec.pending} pending</span>}
                          {ec.rejected > 0 && <span className="li-rej">· ✕ {ec.rejected} rejected</span>}
                        </>
                      : <span className={"li-cert " + (ec.s1any && ec.s2any ? "both" : ec.s1any ? "s1" : "s2")}>· ✓ {ec.s1any && ec.s2any ? "Step 1 & 2" : ec.s1any ? "Step 1" : "Step 2"}</span>}
                    {readyNoCanon(e) && <span className="li-nocanon">· ⚠ no canonical</span>}
                  </div>
                  {(() => { const v = valueOverlap(e); return v.comparable ? (
                    <div className="li-vov">≡ {v.match} match{v.clientOnly ? <span className="li-vc"> · +{v.clientOnly} client-only</span> : null}{v.profileOnly ? <span className="li-vp"> · +{v.profileOnly} data-only</span> : null}</div>
                  ) : null; })()}
                </button>
              );
            })}
            {visible.length === 0 && <div className="lew-empty">No entities in this view.</div>}
          </div>
        </div>

        <div className="lew-editor">
          {!selected ? <div className="lew-empty big">Select or create a lookup entity.</div> : (
            <EntityEditor entity={selected} buckets={buckets} busy={busy} act={act} allEntities={entities}
              proposals={data?.authoritative_proposals || []} cert={cert} onGoStep={onGoStep}
              />
          )}
        </div>
      </div>
      {showHarmonize && <EntityHarmonizePanel entityId={entityId} onApplied={load} onClose={() => setShowHarmonize(false)} />}
    </div>
  );
}

const EXTRA = `
.lew-tools{align-items:center;gap:10px}
.lew-tools-t{font-size:12.5px;color:#5d6d68}
.lew-more{position:relative}
.lew-menu{position:absolute;right:0;top:calc(100% + 4px);background:#fff;border:1px solid #dde3df;border-radius:9px;box-shadow:0 8px 24px rgba(20,32,43,.12);z-index:20;min-width:210px;overflow:hidden}
.lew-menu button{display:block;width:100%;text-align:left;border:none;background:transparent;padding:9px 13px;font-size:12.5px;cursor:pointer;font-family:inherit}
.lew-menu button:hover{background:#f7f9f7}
.lew-queue{display:flex;align-items:center;gap:6px;padding:10px 14px;margin-bottom:10px;background:#fdf8ec;border:1px solid #ecdca8;border-radius:10px;font-size:13px;color:#7a6320;cursor:pointer}
.lew-queue:hover{background:#fcf4e1}
.lew-queue-go{margin-left:auto;font-weight:600;color:#8a6d3b}
.lew-queue-help{display:flex;align-items:center;gap:8px;padding:8px 14px;margin-bottom:10px;background:#f6f9fb;border:1px solid #e3ebf0;border-radius:10px;font-size:12.5px;color:#5f6e7d}
.li-needs{background:#fdf8ec;color:#8a6d3b}
.li-ready{background:#e6f4ee;color:#2f7a4f}
.lew-src{margin-left:6px;font-size:11.5px;border:1px solid #cbd5e0;border-radius:20px;padding:3px 8px;color:#5f6e7d;cursor:pointer;max-width:170px}

.lew-tools{display:flex;align-items:center;gap:8px;padding:2px 2px 12px}
.lew-attn{display:flex;align-items:center;gap:9px;flex-wrap:wrap;font-size:12.5px;color:#7c5310;background:var(--wait-bg);border:1px solid #eBd6a8;border-left:3px solid var(--wait);padding:9px 14px}
.lew-attn b{color:#5c3d0b}
.lew-attn-b{border:1px solid var(--wait);background:#fff;color:#7c5310;border-radius:6px;padding:3px 10px;font-size:11.5px;cursor:pointer;font-family:inherit}
.lew-attn-b:hover{background:var(--wait-bg)}
.lew .pill.attn{border-color:var(--wait);color:var(--wait)}
.li-rej{color:#991b1b;font-weight:600}
.li-attn{color:var(--warn);font-weight:600}.li-ok{color:var(--gov)}
.li-cert{font-weight:600}.li-cert.both{color:var(--gov)}.li-cert.s1{color:var(--infer)}.li-cert.s2{color:#0f766e}
.li-nocanon{color:var(--wait);font-weight:600}
.lew-nv{display:flex;align-items:center;gap:10px;flex-wrap:wrap;font-size:12.5px;color:#7c5310;background:var(--wait-bg);border:1px solid #eBd6a8;border-left:3px solid var(--wait);padding:9px 14px}
.lew-nv b{color:#5c3d0b}
.lew-nv-b{border:1px solid var(--wait);background:#fff;color:#7c5310;border-radius:6px;padding:3px 10px;font-size:11.5px;cursor:pointer;font-family:inherit}
.lew-nv-b:hover{background:var(--wait-bg)}
.li-vov{font-size:10.5px;color:var(--tx3);margin-top:2px}
.li-vc{color:var(--wait)}.li-vp{color:var(--warn)}
`;
